import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, AlertCircle } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { supabase, checkSupabaseConnection } from '../lib/supabase';
import EstimateResults from '../components/EstimateResults';
import TerritoryBreakdown from '../components/TerritoryBreakdown';
import PDFDocument from '../components/PDFDocument';

interface EstimateData {
  id: string;
  title: string;
  director: string;
  actors: any[];
  territories: any;
  estimate_data: any;
}

const EstimateView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState<EstimateData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<boolean | null>(null);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        if (!id) {
          throw new Error('No estimate ID provided');
        }

        // Check Supabase connection first
        const isConnected = await checkSupabaseConnection();
        setConnectionStatus(isConnected);

        if (!isConnected) {
          throw new Error('Unable to connect to database');
        }

        const { data, error: fetchError } = await supabase
          .from('estimates')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) {
          console.error('Supabase fetch error:', fetchError);
          throw new Error('Failed to fetch estimate');
        }
        
        if (!data) {
          throw new Error('Estimate not found');
        }

        setEstimate(data);
      } catch (err: any) {
        console.error('Error fetching estimate:', err);
        setError(err.message);
        // Redirect to home page after 3 seconds if estimate is not found
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading estimate...</div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {error || 'Estimate not found'}
          </h2>
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              Redirecting to home page in a few seconds...
            </p>
            {connectionStatus === false && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg max-w-lg mx-auto mb-4">
                <p className="font-medium">Database Connection Error</p>
                <p className="text-sm">Please ensure you are connected to Supabase</p>
              </div>
            )}
          </div>
          <Link
            to="/"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Estimator
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://c32e022225eda3f5b7ba82cc03e373d6.cdn.bubble.io/f1739203314831x826979416146093000/Cinelaunch%20-%20Digital%20Rocket.png?_gl=1*qgthuz*_gcl_au*MTAyNzEzNzAzOS4xNzM1NDI3MTkz*_ga*NDc0MDQxMTk5LjE3Mjc3OTY1NjY.*_ga_BFPVR2DEE2*MTczOTIwMzI4NC44My4xLjE3MzkyMDMzMDIuNDIuMC4w" 
                alt="CineLaunch Logo" 
                className="h-10 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-900">Film Value Estimate</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Estimator
              </Link>
              <PDFDownloadLink
                document={
                  <PDFDocument
                    title={estimate.title}
                    director={estimate.director}
                    territories={estimate.territories}
                  />
                }
                fileName={`${estimate.title || 'estimate'}-sales-estimate.pdf`}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                {({ loading: pdfLoading }) => (
                  <>
                    <Download className="h-5 w-5 mr-2" />
                    {pdfLoading ? 'Generating PDF...' : 'Export PDF'}
                  </>
                )}
              </PDFDownloadLink>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-4 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4">Project Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <div className="text-gray-900">{estimate.title}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Director
                  </label>
                  <div className="text-gray-900">{estimate.director || 'Not specified'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cast
                  </label>
                  <div className="space-y-2">
                    {estimate.actors.map((actor) => (
                      <div
                        key={actor.id}
                        className="flex items-center space-x-2 bg-indigo-50 px-3 py-1 rounded-full"
                      >
                        {actor.profilePath && (
                          <img
                            src={actor.profilePath}
                            alt={actor.name}
                            className="w-6 h-6 rounded-full"
                          />
                        )}
                        <span className="text-sm font-medium">{actor.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-8 space-y-6">
            <EstimateResults estimate={estimate.estimate_data} />
            <TerritoryBreakdown
              title={estimate.title}
              director={estimate.director}
              actor={estimate.actors[0]}
              territories={estimate.territories}
              baseValues={estimate.territories}
              onValueChange={() => {}} // Read-only view
              readOnly={true}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default EstimateView;